import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { ButtonBase, SxProps } from '@mui/material'
import clsx from 'clsx'
import dayjs from 'dayjs'

import { Box } from '@/components/Layout'
import { Text } from '@/components/Data'
import { DATE_PICKER_MAX_YEAR, DATE_PICKER_MIN_YEAR } from '../DateRangePicker.data'
import { dateRangePickerYearsClasses } from './DateRangePickerYears.data'
import { dateRangePickerYearsStyles as styles } from './DateRangePickerYears.style'
import { DateRangePickerYearsProps } from './DateRangePickerYears.types'

function DateRangePickerYears({
  date,
  disablePastDates,
  disableFutureDates,
  minDate,
  maxDate,
  onYearSelect,
  wrapperProps = {},
  yearWrapperProps = {},
  buttonProps = {},
}: DateRangePickerYearsProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const selectedYearRef = useRef<HTMLButtonElement>(null)

  const scrollToSelectedYear = useCallback(() => {
    if (!selectedYearRef?.current || !wrapperRef?.current) return

    const parent = wrapperRef.current
    const child = selectedYearRef.current

    // Scroll so the selected year is centered in the parent
    const parentRect = parent.getBoundingClientRect()
    const childRect = child.getBoundingClientRect()
    parent.scrollTop +=
      childRect.top - parentRect.top - parent.clientHeight / 2 + child.clientHeight / 2
  }, [])

  const years = useMemo(() => {
    const todayYear = dayjs().year()
    let minYear = minDate ? dayjs(minDate).year() : DATE_PICKER_MIN_YEAR
    let maxYear = maxDate ? dayjs(maxDate).year() : DATE_PICKER_MAX_YEAR

    if (disablePastDates) minYear = todayYear
    if (disableFutureDates) maxYear = todayYear

    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i)
  }, [minDate, maxDate, disablePastDates, disableFutureDates])

  useEffect(() => {
    scrollToSelectedYear()
  }, [years, date, scrollToSelectedYear])

  // props extraction
  const { sx: wrapperSx, ...otherWrapperProps } = wrapperProps
  const { sx: yearWrapperSx, ...otherYearWrapperProps } = yearWrapperProps
  const { sx: buttonSx, className: buttonClassName = '', ...otherButtonProps } = buttonProps

  return (
    <Box sx={[styles.wrapper, wrapperSx] as SxProps} ref={wrapperRef} {...otherWrapperProps}>
      <Box sx={[styles.yearsWrapper, yearWrapperSx] as SxProps} {...otherYearWrapperProps}>
        {years.map((year) => (
          <ButtonBase
            key={year}
            ref={date.year() === year ? selectedYearRef : undefined}
            className={clsx({
              [dateRangePickerYearsClasses.selected]: date.year() === year,
              [buttonClassName]: Boolean(buttonClassName),
            })}
            sx={[styles.button, buttonSx] as SxProps}
            onClick={() => onYearSelect(year)}
            {...otherButtonProps}>
            <Text sx={styles.text}>{year}</Text>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  )
}

export default memo(DateRangePickerYears)
